/**
 * Media Capture Component
 *
 * Mobile-optimized media capture with:
 * - Camera integration (back camera for inspection photos/videos)
 * - Photo and video preview and management
 * - Caption input
 * - Gallery view for existing media
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Paper,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';

interface Photo {
  id: string;
  url: string;
  thumbnailUrl?: string;
  caption?: string;
  mediaType?: 'image' | 'video';
  duration?: number; // Duration in seconds for videos
  uploadedAt?: string;
}

interface PhotoCaptureProps {
  photos: Photo[];
  onCapture?: (file: File) => Promise<void>;
  onDelete?: (photoId: string) => Promise<void>;
  onCaptionChange?: (photoId: string, caption: string) => void;
  disabled?: boolean;
  maxPhotos?: number;
  allowVideo?: boolean; // Allow video capture
  maxVideoDuration?: number; // Max video duration in seconds
  itemName?: string;
}

export function PhotoCapture({
  photos,
  onCapture,
  onDelete,
  onCaptionChange,
  disabled = false,
  maxPhotos = 10,
  allowVideo = true,
  maxVideoDuration: _maxVideoDuration = 60,
  itemName,
}: PhotoCaptureProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [previewFile, setPreviewFile] = useState<{ file: File; url: string; mediaType: 'image' | 'video' } | null>(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [deletePhotoId, setDeletePhotoId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = photos.length < maxPhotos;

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, mediaType: 'image' | 'video' = 'image') => {
      const file = event.target.files?.[0];
      if (file && onCapture) {
        // Show preview before upload
        const previewUrl = URL.createObjectURL(file);
        setPreviewFile({ file, url: previewUrl, mediaType });
        setCaption('');
      }
      // Reset input
      event.target.value = '';
    },
    [onCapture]
  );

  const handleConfirmUpload = async () => {
    if (!previewFile || !onCapture) return;

    setIsUploading(true);
    try {
      await onCapture(previewFile.file);
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
      setCaption('');
    } catch {
      notify.error(tc('error'), t('media.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (previewFile) {
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
      setCaption('');
    }
  };

  const handleDelete = async (photoId: string) => {
    if (onDelete) {
      await onDelete(photoId);
    }
    setSelectedPhoto(null);
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  const openVideoCamera = () => {
    videoInputRef.current?.click();
  };

  return (
    <Box>
      {/* Photo Grid */}
      {photos.length > 0 && (
        <ImageList
          cols={3}
          gap={8}
          sx={{
            width: '100%',
            mb: 2,
            '& .MuiImageListItem-root': {
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              borderRadius: 1,
            },
          }}
        >
          {photos.map((photo) => (
            <ImageListItem
              key={photo.id}
              onClick={() => setSelectedPhoto(photo)}
              sx={{
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <img
                src={photo.thumbnailUrl || photo.url}
                alt={photo.caption || (photo.mediaType === 'video' ? t('media.inspectionVideo') : t('media.inspectionPhoto'))}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              {photo.mediaType === 'video' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: '50%',
                    p: 0.5,
                  }}
                >
                  <PlayCircleIcon sx={{ fontSize: 32 }} />
                </Box>
              )}
              {photo.caption && (
                <ImageListItemBar
                  title={photo.caption}
                  sx={{
                    '& .MuiImageListItemBar-title': {
                      fontSize: '0.65rem',
                    },
                  }}
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Add Photo/Video Buttons */}
      {canAddMore && !disabled && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={openCamera}
            sx={{
              flex: 1,
              minWidth: 120,
              minHeight: 48,
              touchAction: 'manipulation',
            }}
          >
            {t('media.camera')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={openGallery}
            sx={{
              flex: 1,
              minWidth: 120,
              minHeight: 48,
              touchAction: 'manipulation',
            }}
          >
            {t('media.gallery')}
          </Button>
          {allowVideo && (
            <Button
              variant="outlined"
              startIcon={<VideocamIcon />}
              onClick={openVideoCamera}
              sx={{
                flex: 1,
                minWidth: 120,
                minHeight: 48,
                touchAction: 'manipulation',
              }}
            >
              {t('media.video')}
            </Button>
          )}
        </Box>
      )}

      {/* Empty State */}
      {photos.length === 0 && !disabled && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'background.default',
            mt: 2,
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('media.noPhotos')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {t('media.captureOrSelectPhotoHint')}
          </Typography>
        </Paper>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileSelect(e, 'image')}
        style={{ display: 'none' }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e, 'image')}
        style={{ display: 'none' }}
      />
      {allowVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          capture="environment"
          onChange={(e) => handleFileSelect(e, 'video')}
          style={{ display: 'none' }}
        />
      )}

      {/* Preview Dialog */}
      <ResponsiveDialog
        mobileTitle={previewFile?.mediaType === 'video' ? t('media.addVideo') : t('media.addPhoto')}
        open={!!previewFile}
        onClose={handleCancelUpload}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {previewFile?.mediaType === 'video' ? t('media.addVideo') : t('media.addPhoto')}
          </Typography>
          <Tooltip title={tc('close')}>
            <IconButton onClick={handleCancelUpload} size="small" aria-label={tc('close')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 0 }}>
          {previewFile && (
            <>
              {previewFile.mediaType === 'video' ? (
                <Box
                  component="video"
                  src={previewFile.url}
                  controls
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={t('media.preview')}
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              )}
              {itemName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('media.item')}: {itemName}
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                placeholder={previewFile.mediaType === 'video' ? t('media.videoCaptionPlaceholder') : t('media.photoCaptionPlaceholder')}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCancelUpload} disabled={isUploading}>
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpload}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
          >
            {isUploading ? tc('loading') : tc('save')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Photo Detail Dialog */}
      <ResponsiveDialog
        mobileTitle={selectedPhoto?.caption || (selectedPhoto?.mediaType === 'video' ? t('media.video') : t('media.photo'))}
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedPhoto && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {selectedPhoto.caption || (selectedPhoto.mediaType === 'video' ? t('media.video') : t('media.photo'))}
              </Typography>
              <Tooltip title={tc('close')}>
                <IconButton onClick={() => setSelectedPhoto(null)} size="small" aria-label={tc('close')}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 0 }}>
              {selectedPhoto.mediaType === 'video' ? (
                <Box
                  component="video"
                  src={selectedPhoto.url}
                  controls
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(100vh - 250px)',
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={selectedPhoto.url}
                  alt={selectedPhoto.caption || t('media.photo')}
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(100vh - 250px)',
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              )}
              {onCaptionChange && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder={selectedPhoto.mediaType === 'video' ? t('media.videoCaption') : t('media.photoCaption')}
                  defaultValue={selectedPhoto.caption || ''}
                  onBlur={(e) => onCaptionChange(selectedPhoto.id, e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1, justifyContent: 'space-between' }}>
              {onDelete && (
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeletePhotoId(selectedPhoto.id)}
                >
                  {tc('delete')}
                </Button>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={tc('fullscreen')}>
                  <IconButton onClick={() => window.open(selectedPhoto.url, '_blank')} aria-label={tc('fullscreen')}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
                <Button variant="contained" onClick={() => setSelectedPhoto(null)}>
                  {tc('close')}
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </ResponsiveDialog>

      {/* Delete Photo Confirmation */}
      <ConfirmDialog
        open={!!deletePhotoId}
        title={tc('delete')}
        message={t('media.confirmDelete')}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={() => {
          if (deletePhotoId) void handleDelete(deletePhotoId);
          setDeletePhotoId(null);
        }}
        onCancel={() => setDeletePhotoId(null)}
      />
    </Box>
  );
}

/**
 * Compact photo preview for item rows
 */
interface PhotoPreviewProps {
  photos: Photo[];
  maxVisible?: number;
  onClick?: () => void;
}

export function PhotoPreview({ photos, maxVisible = 3, onClick }: PhotoPreviewProps) {
  if (photos.length === 0) return null;

  const visiblePhotos = photos.slice(0, maxVisible);
  const remaining = photos.length - maxVisible;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {visiblePhotos.map((photo, index) => (
        <Box
          key={photo.id}
          component="img"
          src={photo.thumbnailUrl || photo.url}
          alt=""
          sx={{
            width: 32,
            height: 32,
            objectFit: 'cover',
            borderRadius: 0.5,
            border: 2,
            borderColor: 'background.paper',
            ml: index > 0 ? -1.5 : 0,
            position: 'relative',
            zIndex: maxVisible - index,
          }}
        />
      ))}
      {remaining > 0 && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 0.5,
            backgroundColor: 'grey.200',
            border: 2,
            borderColor: 'background.paper',
            ml: -1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            +{remaining}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
